import { ArrowLeft, CheckCircle, CreditCard, Loader2, Lock, QrCode, ReceiptText } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { getInvoiceById } from "../../api/action/invoice.jsx";
import { getOrdersByOrderId, updateOrder } from "../../api/action/order.jsx";
import { fetchAccountDetails, fetchUser, initiatePayment, setReturnURL } from "../../api/action/user";
import Loading from "../LandingPage/InstitutionDomain/Enquiry/components/Loading";
import {
    buildZellePendingTransaction,
    getDefaultGatewayPaymentMethod,
    getGatewayCredentials,
    getGatewayProvider,
    getManualPaymentDisplayDetails,
    getOfflinePaymentSavedMessage,
    getSelectedPaymentOption,
    getSelectablePaymentOptions,
    getCurrencyForCountry,
    getOfflineOrderStatus,
    isCashPaymentOption,
    normalizeOfflineReferenceId,
    getZellePaymentMethod,
    requiresOfflineReference,
    resolveInvoiceCustomerAccountId,
    resolveInvoicePaymentCompany
} from "./paymentMethodUtils";

const CASH_PAYMENT_METHODS = new Set(["CASH", "CASH_ON_DELIVERY", "COD", "CASH_PAYMENT"]);
const BLANK_CASH_PAYMENT_METHODS = new Set(["", "N/A", "NA", "NONE", "NULL", "UNDEFINED", "-", "\u2014"]);
const EMPTY_PAYMENT_METHODS = [];
const EMPTY_PAYMENT_USER = {};

const getResponseList = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.payload)) return response.payload;
    return [];
};

const normalizePaymentMethodCode = (value = "") => (
    String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_")
);

const isCashPaymentMethodCode = (value, treatBlankAsCash = false) => {
    if (value === undefined || value === null) return false;

    const normalizedValue = normalizePaymentMethodCode(value);
    return (treatBlankAsCash && BLANK_CASH_PAYMENT_METHODS.has(normalizedValue))
        || CASH_PAYMENT_METHODS.has(normalizedValue);
};

const hasCashPaymentTransaction = (transactionList = []) => (
    Array.isArray(transactionList) && transactionList.some((transaction) => {
        const transactionPaymentMethod = transaction?.paymentMethod;
        const hasNestedPaymentType = transactionPaymentMethod
            && typeof transactionPaymentMethod === "object"
            && Object.prototype.hasOwnProperty.call(transactionPaymentMethod, "paymentMethodType");
        const hasNestedPaymentName = transactionPaymentMethod
            && typeof transactionPaymentMethod === "object"
            && Object.prototype.hasOwnProperty.call(transactionPaymentMethod, "name");
        const hasDirectPaymentMethodType = Object.prototype.hasOwnProperty.call(transaction || {}, "paymentMethodType");
        const hasDirectPaymentMethod = Object.prototype.hasOwnProperty.call(transaction || {}, "paymentMethod");

        return (hasNestedPaymentType && isCashPaymentMethodCode(transactionPaymentMethod?.paymentMethodType, true))
            || (hasNestedPaymentName && isCashPaymentMethodCode(transactionPaymentMethod?.name, true))
            || (hasDirectPaymentMethodType && isCashPaymentMethodCode(transaction?.paymentMethodType, true))
            || (hasDirectPaymentMethod && isCashPaymentMethodCode(transaction?.paymentMethod, true));
    })
);

const isCashPaymentOrder = (orderData) => {
    if (!orderData) return false;
    const hasPaymentMethod = Object.prototype.hasOwnProperty.call(orderData, "paymentMethod");
    const hasOrderPaymentMethod = Object.prototype.hasOwnProperty.call(orderData, "orderPaymentMethod");

    return (hasPaymentMethod && isCashPaymentMethodCode(orderData.paymentMethod, true))
        || (hasOrderPaymentMethod && isCashPaymentMethodCode(orderData.orderPaymentMethod, true))
        || hasCashPaymentTransaction(orderData.transactionList);
};

// These are hoisted to module scope (instead of being declared inside OtpPayment) so they keep a
// stable component identity across re-renders. Declaring them inside the component body previously
// caused React to unmount/remount this whole subtree on every keystroke in the Zelle reference field,
// which dropped input focus and reset the page scroll to top.
const InlineDivider = () => <div className="my-2 border-t border-gray-200" />;

const NativeAlert = ({ open, severity = "error", children, topClass = "top-4", onClose }) => {
    if (!open) return null;

    const palette = severity === "success"
        ? "border-green-200 bg-green-50 text-green-700"
        : severity === "warning"
            ? "border-amber-200 bg-amber-50 text-amber-800"
            : "border-red-200 bg-red-50 text-red-700";

    return (
        <div className={`fixed left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-lg border px-4 py-3 shadow-xl ${topClass} ${palette}`}>
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    {severity === "warning" && <Lock size={18} />}
                    <span>{children}</span>
                </div>
                {onClose && (
                    <button
                        type="button"
                        className="rounded px-2 py-1 text-sm font-semibold hover:bg-white/50"
                        onClick={onClose}
                    >
                        Close
                    </button>
                )}
            </div>
        </div>
    );
};

const NativeModal = ({ open, title, children, footer, onClose, maxWidthClass = "max-w-md" }) => {
    if (!open) return null;

    const modalTitle = title || "Dialog";
    const titleId = `${modalTitle.replace(/\s+/g, "-").toLowerCase()}-title`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <button
                type="button"
                className="absolute inset-0 h-full w-full bg-black/40"
                onClick={onClose}
                aria-label={`Close ${modalTitle}`}
            />
            <div
                className={`relative w-full ${maxWidthClass} overflow-hidden rounded-2xl bg-white shadow-2xl`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <div
                    id={titleId}
                    className="border-b border-gray-200 px-6 py-4 text-xl font-bold text-gray-900"
                >
                    {modalTitle}
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                    {children}
                </div>
                {footer && (
                    <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

const getPaymentOptionIcon = (optionValue, availablePaymentOptions) => {
    const paymentOptionConfig = availablePaymentOptions.find((option) => option.value === optionValue);

    if (requiresOfflineReference(paymentOptionConfig)) {
        return <QrCode className="text-violet-600" />;
    }

    return <CreditCard className="text-blue-600" />;
};

const PaymentMethodSelector = ({
    availablePaymentOptions,
    paymentMethod,
    onPaymentMethodChange,
    selectedPaymentOption,
    manualPaymentDetails,
    onOpenManualPaymentModal
}) => (
    <fieldset className="w-full">
        <legend className="mb-3 text-lg font-semibold text-gray-700">
            Select Payment Method
        </legend>
        <div
            role="radiogroup"
            aria-label="payment method"
            className="space-y-4"
        >
            {availablePaymentOptions.map((option) => (
                <label
                    key={option.value}
                    className={`flex cursor-pointer items-center rounded-lg border p-4 transition ${paymentMethod === option.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-blue-500"
                        }`}
                >
                    <input
                        type="radio"
                        name="paymentMethod"
                        value={option.value}
                        checked={paymentMethod === option.value}
                        onChange={(event) => onPaymentMethodChange(event.target.value)}
                        className="mr-3 h-4 w-4 accent-blue-600"
                    />
                    <span className="mr-3">{getPaymentOptionIcon(option.value, availablePaymentOptions)}</span>
                    <span>
                        <span className="block">{option.title}</span>
                        <span className="block text-xs text-gray-500">{option.description}</span>
                    </span>
                </label>
            ))}
        </div>
        {availablePaymentOptions.length === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                No online payment options are available for this order.
            </div>
        )}
        {requiresOfflineReference(selectedPaymentOption) && (
            <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 p-4">
                <p className="text-sm font-semibold text-gray-900">
                    {manualPaymentDetails.title} recipient: {manualPaymentDetails.recipientName}
                </p>
                <p className="mt-1 text-xs text-gray-600">{manualPaymentDetails.referenceLabel}</p>
                <button
                    type="button"
                    className="mt-2 text-sm font-semibold text-violet-700 underline"
                    onClick={onOpenManualPaymentModal}
                >
                    {manualPaymentDetails.qrImageUrl ? `Open ${manualPaymentDetails.title} QR` : `Open ${manualPaymentDetails.title} Details`}
                </button>
            </div>
        )}
    </fieldset>
);

const CashPaymentDialog = ({ open, isSubmitting, onClose, onConfirm }) => (
    <NativeModal
        open={open}
        onClose={() => !isSubmitting && onClose()}
        title="Confirm Cash Payment"
        footer={(
            <>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    Close
                </button>
                <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={onConfirm}
                    className="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isSubmitting ? "Saving..." : "Confirm"}
                </button>
            </>
        )}
    >
        <span className="text-sm text-gray-600">
            This will keep the order in payment pending status until the cash payment is collected and verified.
        </span>
    </NativeModal>
);

const ZellePaymentDialog = ({
    open,
    onClose,
    isSubmitting,
    manualPaymentDetails,
    currencySymbol,
    totalAmt,
    orderNumber,
    referenceId,
    onReferenceIdChange,
    normalizedReferenceId,
    onConfirm
}) => (
    <NativeModal
        open={open}
        onClose={() => !isSubmitting && onClose()}
        title={manualPaymentDetails.dialogTitle}
        maxWidthClass="max-w-[470px]"
        footer={(
            <>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    Close
                </button>
                <button
                    type="button"
                    disabled={isSubmitting || !normalizedReferenceId}
                    onClick={onConfirm}
                    className="inline-flex items-center rounded-lg bg-violet-600 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isSubmitting ? <Loader2 size={18} className="mr-2 animate-spin" /> : <QrCode size={18} className="mr-2" />}
                    {isSubmitting ? "Saving..." : manualPaymentDetails.confirmLabel}
                </button>
            </>
        )}
    >
        <div className="rounded-2xl bg-gradient-to-br from-violet-700 via-fuchsia-600 to-indigo-700 p-5 text-white">
            <p className="text-xs uppercase tracking-[0.2em] text-white/70">{manualPaymentDetails.title}</p>
            <p className="mt-1 text-xl font-bold">{manualPaymentDetails.recipientName}</p>
            <p className="mt-1 text-sm text-white/80">{manualPaymentDetails.referenceLabel}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white/15 p-3">
                    <p className="text-xs text-white/70">Amount Due</p>
                    <p className="text-base font-bold">{currencySymbol}{Number(totalAmt || 0).toFixed(2)}</p>
                </div>
                <div className="rounded-xl bg-white/15 p-3">
                    <p className="text-xs text-white/70">Order Ref</p>
                    <p className="text-base font-bold">{orderNumber || 'Pending'}</p>
                </div>
            </div>
        </div>

        <div className="mt-5 rounded-2xl border border-dashed border-violet-200 bg-violet-50 p-5 text-center">
            {manualPaymentDetails.qrImageUrl ? (
                <img
                    src={manualPaymentDetails.qrImageUrl}
                    alt={`${manualPaymentDetails.title} QR`}
                    className="mx-auto h-44 w-44 rounded-2xl border border-violet-100 bg-white p-2 object-cover"
                />
            ) : (
                <div className="mx-auto flex h-44 w-44 items-center justify-center rounded-2xl border border-violet-100 bg-white p-4 text-center text-sm text-slate-500">
                    Use the saved recipient details above to complete the payment.
                </div>
            )}
            <p className="mt-4 text-sm text-gray-700">
                {manualPaymentDetails.qrImageUrl
                    ? "Scan the QR code, pay the exact amount, and keep the order number in your payment memo."
                    : "Use the saved recipient details, pay the exact amount, and keep the order number in your payment memo."}
            </p>
        </div>

        <div className="mt-5 text-sm text-gray-600 leading-7">
            <p className="font-semibold text-gray-900 mb-1">How to complete this payment</p>
            <p>1. Open your payment app or banking app.</p>
            <p>2. {manualPaymentDetails.qrImageUrl ? "Scan the QR code or use the recipient details above." : "Use the recipient details above."}</p>
            <p>3. Send the full amount and keep your confirmation for manual verification.</p>
        </div>

        <label className="mt-5 block text-sm font-semibold text-gray-700">
            {manualPaymentDetails.title} Reference ID
            <input
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:bg-gray-100"
                placeholder={`Enter the ${manualPaymentDetails.title} confirmation ID`}
                value={referenceId}
                onChange={(event) => onReferenceIdChange(event.target.value)}
                disabled={isSubmitting}
            />
        </label>
        <p className="mt-1 text-xs text-gray-500">{manualPaymentDetails.helperText}</p>
    </NativeModal>
);

const OtpPayment = () => {
    // State declarations
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [showBalanceAlert, setShowBalanceAlert] = useState(false);
    const [showNavigationDialog, setShowNavigationDialog] = useState(false);
    const [blockNavigation, setBlockNavigation] = useState(false);
    const [alertMsg, setAlertMsg] = useState({
        open: false,
        message: "",
        severity: "error",
    });
    const [, setActiveStep] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [showCashConfirmDialog, setShowCashConfirmDialog] = useState(false);
    const [showZelleModal, setShowZelleModal] = useState(false);
    const [isSubmittingOfflinePayment, setIsSubmittingOfflinePayment] = useState(false);
    const [zelleReferenceId, setZelleReferenceId] = useState("");

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: invoiceIdFromRoute } = useParams();
    const [routeInvoice, setRouteInvoice] = useState(null);
    const [routeOrder, setRouteOrder] = useState(null);

    // Shipping address state
    const [shippingAddress, setShippingAddress] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        flatApartment: "",
        street1: "",
        street2: "",
        city: "",
        state: "",
        country: "",
        pinCode: "",
    });

    // Selectors
    const userDetail = useSelector((state) => state?.auth?.data);
    const {
        order: stateOrder,
        user: stateUser,
        invoiceId: stateInvoiceId,
        invoice: stateInvoice,
        originalPaymentMethod
    } = location.state || {};
    const invoice = routeInvoice || stateInvoice;
    const order = routeOrder || stateOrder;
    const user = useMemo(
        () => stateUser || invoice?.user || EMPTY_PAYMENT_USER,
        [invoice?.user, stateUser]
    );
    const invoiceId = stateInvoiceId || invoiceIdFromRoute || invoice?.primaryId;

    // Store data in localStorage
    useEffect(() => {
        if (order?.totalAmount) {
            localStorage.setItem('orderDetails', order.totalAmount.toString());
        }
        if (user?.primaryId) {
            localStorage.setItem("applicantUserId", user.primaryId);
        }
        if (user?.firstName || user?.lastName) {
            localStorage.setItem("applicantName", `${user.firstName || ''} ${user.lastName || ''}`);
        }
        if (order?.totalAmount) {
            localStorage.setItem("applicantTotalAmount", order.totalAmount.toString());
        }
        if (order?.primaryId) {
            localStorage.setItem("applicantOrderId", order.primaryId);
        }
        if (invoiceId) {
            localStorage.setItem("invoiceId", invoiceId);
        }
        if (invoice) {
            localStorage.setItem("invoiceDet", invoice);
        }
    }, [order, user, invoiceId, invoice]);

    const companyState = useSelector((state) => state.companyReducer.company);
    const reduxOrderDetail = order;
    const businessdomain = localStorage.getItem("domainType");
    const storedCompanyDetails = companyState;
    const paymentCompanyDetails = resolveInvoicePaymentCompany(invoice, order, storedCompanyDetails);
    const companyOperatingLocation = paymentCompanyDetails?.operatingLocation?.code || 'IN';
    const currentPageName = location.pathname.split("/").pop() || "Home";
    const paymentUser = useMemo(
        () => ((userDetail?.primaryId || userDetail?.email) ? userDetail : (user || EMPTY_PAYMENT_USER)),
        [user, userDetail]
    );
    const companyPaymentMethods = paymentCompanyDetails?.account?.paymentMethods || EMPTY_PAYMENT_METHODS;
    const currentOrderDetails = orderDetails?.orderDetails?.[0];
    const shouldHideCashPaymentOption = isCashPaymentOrder(currentOrderDetails)
        || isCashPaymentOrder(order)
        || isCashPaymentOrder(invoice?.order)
        || isCashPaymentMethodCode(originalPaymentMethod, Object.prototype.hasOwnProperty.call(location.state || {}, "originalPaymentMethod"))
        || isCashPaymentMethodCode(invoice?.paymentMethod, Object.prototype.hasOwnProperty.call(invoice || {}, "paymentMethod"));
    const paymentOptions = useMemo(() => getSelectablePaymentOptions(
        companyOperatingLocation,
        companyPaymentMethods,
        paymentCompanyDetails
    ), [companyOperatingLocation, companyPaymentMethods, paymentCompanyDetails]);
    const availablePaymentOptions = useMemo(() => (
        shouldHideCashPaymentOption
            ? paymentOptions.filter((option) => !isCashPaymentOption(option))
            : paymentOptions
    ), [paymentOptions, shouldHideCashPaymentOption]);
    const isSelectedPaymentMethodAvailable = availablePaymentOptions.some((option) => option.value === paymentMethod);
    const selectedPaymentOption = getSelectedPaymentOption(availablePaymentOptions, paymentMethod);
    const manualPaymentOption = requiresOfflineReference(selectedPaymentOption)
        ? selectedPaymentOption
        : availablePaymentOptions.find((option) => option.requiresReference) || null;
    const zellePaymentMethod = getZellePaymentMethod(companyPaymentMethods);
    const manualPaymentDetails = getManualPaymentDisplayDetails(
        paymentCompanyDetails,
        manualPaymentOption,
        orderDetails?.orderDetails?.[0]?.number || order?.number
    );
    const gatewayCredentials = getGatewayCredentials(companyPaymentMethods);
    const normalizedZelleReferenceId = normalizeOfflineReferenceId(zelleReferenceId);

    const [totalAmt, setTotalAmt] = useState(0);
    const [, setTotalAmount] = useState(0);
    const shippingCharge = 0;
    const subtotal = totalAmt;
    const tax = 0;
    const isPaymentActionDisabled = loading || isProcessingPayment || totalAmt <= 0 || !isSelectedPaymentMethodAvailable;

    // Set default payment method based on country
    useEffect(() => {
        const defaultGatewayMethod = getDefaultGatewayPaymentMethod(companyOperatingLocation, companyPaymentMethods);
        const nextPaymentMethod = availablePaymentOptions.find((option) => option.value === defaultGatewayMethod)?.value
            || availablePaymentOptions[0]?.value
            || "";

        setPaymentMethod((currentMethod) => (
            availablePaymentOptions.some((option) => option.value === currentMethod)
                ? currentMethod
                : nextPaymentMethod
        ));
    }, [companyOperatingLocation, companyPaymentMethods, availablePaymentOptions]);

    // Transform order data to expected format
    const transformOrderData = (orderData) => {
        if (!orderData) return null;

        return {
            orderDetails: [{
                primaryId: orderData.primaryId,
                number: orderData.number || `ORD-${orderData.primaryId}`,
                totalAmount: orderData.totalAmount || 0,
                dueAmount: orderData.dueAmount || orderData.totalAmount || 0,
                ...orderData
            }]
        };
    };

    // Persist order data to localStorage
    const persistOrderData = (orderData) => {
        try {
            if (orderData) {
                localStorage.setItem('persistedOrder', JSON.stringify({
                    ...orderData,
                    timestamp: new Date().getTime()
                }));
            }
        } catch {
        }
    };

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                // First check for order in location state
                if (reduxOrderDetail) {
                    const transformedOrder = transformOrderData(reduxOrderDetail);
                    setOrderDetails(transformedOrder);
                    const amount = order?.totalAmount || 0;
                    setTotalAmt(amount);
                    setTotalAmount(transformedOrder.orderDetails[0].dueAmount);
                    persistOrderData(transformedOrder);
                    return;
                }

                if (invoiceIdFromRoute) {
                    setLoading(true);
                    const invoiceData = await dispatch(getInvoiceById(invoiceIdFromRoute));
                    let fetchedOrder = invoiceData?.order || null;

                    if (invoiceData?.order?.primaryId) {
                        const orderData = await dispatch(getOrdersByOrderId(invoiceData.order.primaryId));
                        fetchedOrder = Array.isArray(orderData) ? orderData[0] : orderData;
                    }

                    const transformedOrder = transformOrderData(fetchedOrder);
                    if (!transformedOrder) {
                        throw new Error("Order details are missing for this invoice.");
                    }

                    const amount = fetchedOrder?.dueAmount ?? fetchedOrder?.totalAmount ?? invoiceData?.amount ?? 0;
                    setRouteInvoice(invoiceData);
                    setRouteOrder(fetchedOrder);
                    setOrderDetails(transformedOrder);
                    setTotalAmt(amount);
                    setTotalAmount(transformedOrder.orderDetails[0].dueAmount);
                    persistOrderData(transformedOrder);
                    return;
                }

                // Then check localStorage for persisted order
                const savedOrder = localStorage.getItem('persistedOrder');
                if (savedOrder) {
                    const parsedOrder = JSON.parse(savedOrder);
                    const oneHourAgo = new Date().getTime() - (60 * 60 * 1000);

                    if (parsedOrder && parsedOrder.timestamp > oneHourAgo) {
                        setOrderDetails(parsedOrder);
                        setTotalAmt(parsedOrder.orderDetails[0].totalAmount);
                        setTotalAmount(parsedOrder.orderDetails[0].dueAmount);
                        return;
                    }
                }

                // If no valid order found
                localStorage.removeItem('persistedOrder');
                setAlertMsg({
                    open: true,
                    message: "Order data is missing or expired. Please restart your order.",
                    severity: "error",
                });
                navigate('/'); // Redirect to home if no order data

            } catch (error) {
                setAlertMsg({
                    open: true,
                    message: error.message || "Error loading order details. Please try again.",
                    severity: "error",
                });
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [dispatch, invoiceIdFromRoute, navigate, order?.totalAmount, reduxOrderDetail]);

    // Update order details when Redux changes
    useEffect(() => {
        if (reduxOrderDetail) {
            const transformedOrder = transformOrderData(reduxOrderDetail);
            setOrderDetails(transformedOrder);
            const amount = order?.totalAmount || 0;
            setTotalAmt(amount);
            setTotalAmount(transformedOrder.orderDetails[0].dueAmount);
            persistOrderData(transformedOrder);
        }
    }, [order?.totalAmount, reduxOrderDetail]);

    useEffect(() => {
        if (paymentUser?.firstName || paymentUser?.email) {
            const address = paymentUser.addressList && paymentUser.addressList[0];
            const newAddress = {
                firstName: paymentUser.firstName || "",
                lastName: paymentUser.lastName || "",
                email: paymentUser.email || "",
                phoneNumber: paymentUser.phoneNumber || "",
                flatApartment: address?.streetName1 || "",
                street1: address?.streetName1 || "",
                street2: address?.streetName2 || "",
                city: address?.city || "",
                state: address?.state || "",
                country: address?.country || "",
                pinCode: address?.zipcode || "",
            };
            setShippingAddress(newAddress);
        }
    }, [paymentUser]);

    // Payment submission handler
    const submitPayment = async (paymentData, protocol, returnURL, countryCode) => {
        setLoading(true);
        setIsProcessingPayment(true);
        setBlockNavigation(true);
        setActiveStep(1);

        try {
            // Set return URL
            await setReturnURL(protocol, returnURL, countryCode);

            // Initiate payment
            const response = await initiatePayment(paymentData);

            setLoading(false);
            setIsProcessingPayment(false);
            setActiveStep(2); // success step

            if (!response || !response.data) {
                throw new Error("Payment gateway did not respond properly");
            }

            const { data } = response;

            // Decide payment URL
            let paymentUrl;

            if (paymentMethod === "stripe" && data.stripeResponse) {
                paymentUrl = data.stripeResponse;
            } else if (paymentMethod === "razorPay" && data.razorPayResponse) {
                paymentUrl = data.razorPayResponse;
            } else if (data.razorPayResponse) {
                paymentUrl = data.razorPayResponse;
            } else if (data.stripeResponse) {
                paymentUrl = data.stripeResponse;
            } else {
                throw new Error("No payment URL received from gateway");
            }

            window.location.href = paymentUrl;

        } catch (err) {
            setLoading(false);
            setIsProcessingPayment(false);
            setActiveStep(0);

            setAlertMsg({
                open: true,
                message: err.message || "Payment processing failed",
                severity: "error",
            });
        }
    };

    const updateOfflineOrderState = (selectedMethod) => {
        setOrderDetails((prev) => {
            if (!prev?.orderDetails?.length) return prev;
            const nextStatus = getOfflineOrderStatus(selectedMethod.value);

            const updatedOrder = {
                ...prev.orderDetails[0],
                currentStatus: nextStatus,
                paymentMethod: selectedMethod.orderPaymentMethod
            };

            const nextOrderDetails = {
                ...prev,
                orderDetails: [updatedOrder]
            };

            persistOrderData(nextOrderDetails);
            return nextOrderDetails;
        });
    };

    const handleOfflinePaymentRequest = async (selectedMethod) => {
        try {
            if (!selectedMethod) {
                throw new Error("Selected payment method is not available.");
            }

            const currentOrder = orderDetails?.orderDetails?.[0];
            if (!currentOrder?.primaryId) {
                throw new Error("Order details are missing.");
            }

            if (requiresOfflineReference(selectedMethod) && !normalizedZelleReferenceId) {
                throw new Error("Enter the Zelle reference ID before continuing.");
            }

            setLoading(true);
            setIsProcessingPayment(true);
            setIsSubmittingOfflinePayment(true);

            const modifiedBy = `${paymentUser?.firstName || ""} ${paymentUser?.lastName || ""}`.trim()
                || paymentUser?.email
                || paymentUser?.phoneNumber
                || "Guest";

            const zelleTransaction = requiresOfflineReference(selectedMethod)
                ? buildZellePendingTransaction({
                    amount: totalAmt,
                    orderPrimaryId: currentOrder.primaryId,
                    accountPrimaryId: paymentUser?.account?.primaryId || currentOrder?.accountId,
                    userPrimaryId: paymentUser?.primaryId || currentOrder?.userPrimaryId || currentOrder?.user?.primaryId,
                    company: storedCompanyDetails,
                    zellePaymentMethod,
                    paymentMethodConfig: selectedMethod.paymentMethodConfig,
                    referenceId: normalizedZelleReferenceId,
                    createdBy: modifiedBy
                })
                : null;
            const nextStatus = getOfflineOrderStatus(selectedMethod.value);

            await dispatch(updateOrder({
                primaryId: Number(currentOrder.primaryId) || currentOrder.primaryId,
                currentStatus: nextStatus,
                paymentMethod: selectedMethod.orderPaymentMethod,
                modifiedBy,
                ...(zelleTransaction ? { transactionList: [zelleTransaction] } : {})
            }, "order_status_update"));

            setShowCashConfirmDialog(false);
      setShowZelleModal(false);
      setZelleReferenceId("");
      updateOfflineOrderState(selectedMethod);
      setAlertMsg({
        open: true,
        message: getOfflinePaymentSavedMessage(selectedMethod),
        severity: "success"
      });

            setTimeout(() => navigate(invoiceId ? `/receipt/view/${invoiceId}` : '/my-bookings'), 1500);
        } catch (error) {
            setAlertMsg({
                open: true,
                message: error.message || `Failed to update the order for ${selectedMethod?.title || "the selected payment method"}.`,
                severity: "error"
            });
        } finally {
            setLoading(false);
            setIsProcessingPayment(false);
            setIsSubmittingOfflinePayment(false);
        }
    };

    const handleCheckout = async () => {
        try {
            const order = orderDetails?.orderDetails?.[0];
            if (!order?.primaryId) {
                throw new Error("Order details are missing. Please restart the checkout process.");
            }
            if (!paymentMethod) {
                throw new Error("Please select a payment method.");
            }
            if (isCashPaymentOption(selectedPaymentOption)) {
                setShowCashConfirmDialog(true);
                return;
            }
            if (requiresOfflineReference(selectedPaymentOption)) {
                setShowZelleModal(true);
                return;
            }

            setLoading(true);
            setIsProcessingPayment(true);

            const userAccountId = resolveInvoiceCustomerAccountId(invoice, order, paymentUser);

            if (!userAccountId) {
                throw new Error("User account information is missing.");
            }

            const isInvoiceCheckout = Boolean(invoiceId || invoice?.primaryId);
            let customerUser = paymentUser;

            if (isInvoiceCheckout) {
                const embeddedCustomerAccount = order?.account || invoice?.order?.account;
                const customerAccount = embeddedCustomerAccount?.userId
                    ? embeddedCustomerAccount
                    : await fetchAccountDetails(userAccountId);
                const customerAccountUserId = customerAccount?.userId;

                if (!customerAccountUserId) {
                    throw new Error("The onboarded company account user is missing.");
                }

                const customerUserResponse = await dispatch(fetchUser({ primaryId: customerAccountUserId }));
                const customerUsers = getResponseList(customerUserResponse);
                customerUser = customerUsers.find((candidate) => (
                    String(candidate?.primaryId) === String(customerAccountUserId)
                )) || customerUsers[0] || null;

                if (!customerUser?.primaryId) {
                    throw new Error("The onboarded company account user details could not be loaded.");
                }
            }

            const userId = customerUser?.primaryId
                || order?.user?.primaryId
                || invoice?.user?.primaryId;
            const companyId = paymentCompanyDetails?.primaryId;
            const companyAccountId = paymentCompanyDetails?.account?.primaryId;
            const customerName = `${customerUser?.firstName || ""} ${customerUser?.lastName || ""}`.trim()
                || "Customer";
            const customerEmail = customerUser?.email || "";
            const companyDomain = paymentCompanyDetails?.domain;

            if (isInvoiceCheckout && !customerEmail) {
                throw new Error("The onboarded company account user email is missing.");
            }

            if (!companyId) {
                throw new Error("Payment gateway company details are missing.");
            }

            if (!companyAccountId) {
                throw new Error("Payment gateway company account details are missing.");
            }
            sessionStorage.removeItem("pendingBooking");
            const gatewayProvider = getGatewayProvider(paymentMethod, companyOperatingLocation, companyPaymentMethods);
            const currency = getCurrencyForCountry(companyOperatingLocation);
            const paymentData = {
                paymentMethod: "PAYMENT_GATEWAY",
                currency: currency,
                countryCode: companyOperatingLocation,
                orderNumber: order.number || order.primaryId,
                amount: totalAmt,
                accountId: userAccountId,
                ...(userId == null ? {} : { userId }),
                transactionType: "PURCHASE",
                orderId: String(order.primaryId),
                paymentGatewayData: {
                    gatewayProvider: gatewayProvider,
                    customerName: customerName,
                    customerEmail: customerEmail,
                    amount: totalAmt,
                    // products: courseNames,
                    // itemCount: lineItems.length,
                    gatewayCredentials
                },
                company: {
                    primaryId: companyId,
                    account: {
                        primaryId: companyAccountId,
                        paymentMethods: paymentCompanyDetails?.account?.paymentMethods || []
                    },
                    domain: companyDomain,
                }
            };

            const protocol = window.location.protocol;
            const returnURL = window.location.host;

            await submitPayment(paymentData, protocol, returnURL, companyOperatingLocation);

        } catch (error) {
            setAlertMsg({
                open: true,
                message: error.message || `Payment failed for ${selectedPaymentOption?.title || "the selected payment method"}. Please try again.`,
                severity: "error",
            });
        } finally {
            setLoading(false);
            setIsProcessingPayment(false);
        }
    };

    // Currency symbol based on location
    const currencySymbol = () => {
        return companyOperatingLocation === "US" ? "$" : "₹";
    };

    // Navigation handler
    const handleBackNavigation = () => {
        if (blockNavigation && !isProcessingPayment) {
            setShowNavigationDialog(true);
        } else {
            navigate(-1);
        }
    };

    const getCheckoutButtonLabel = () => {
        if (isSubmittingOfflinePayment) return "Saving...";
        if (isProcessingPayment) return "Processing Payment...";
        if (isCashPaymentOption(selectedPaymentOption)) return "Confirm Cash Payment";
        if (requiresOfflineReference(selectedPaymentOption)) return `Open ${selectedPaymentOption?.title || "Payment"} Instructions`;
        return totalAmt <= 0 ? "No Payment Required" : "Complete Payment";
    };

    useEffect(() => {
        if (!showBalanceAlert) return undefined;

        const timer = setTimeout(() => setShowBalanceAlert(false), 6000);
        return () => clearTimeout(timer);
    }, [showBalanceAlert]);

    useEffect(() => {
        if (!alertMsg.open) return undefined;

        const timer = setTimeout(() => {
            setAlertMsg((current) => ({ ...current, open: false }));
        }, 6000);
        return () => clearTimeout(timer);
    }, [alertMsg.open]);

    // Loading state if order details aren't available
    if (!orderDetails) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
                <div className="text-center max-w-md">
                    <Loader2 size={60} className="mx-auto animate-spin text-blue-500" />
                    <p className="mt-6 text-lg text-gray-700 font-medium">
                        {alertMsg.open ? alertMsg.message : "Loading your order details..."}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <button
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md flex items-center"
                            onClick={() => window.location.reload()}
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh Page
                        </button>
                        <button
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition shadow-md flex items-center"
                            onClick={() => navigate('/')}
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            Return Home
                        </button>
                    </div>

                    <NativeAlert
                        open={alertMsg.open}
                        severity={alertMsg.severity}
                        onClose={() => setAlertMsg({ ...alertMsg, open: false })}
                    >
                        {alertMsg.message}
                    </NativeAlert>
                </div>
            </div>
        );
    }

    // Institution Domain UI
    if (businessdomain === "INSTITUTION") {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#F4F1EB] to-[#ECE7DA] py-12 px-4 sm:px-6 lg:px-8 font-serif">
                <NativeAlert
                    open={showBalanceAlert}
                    severity="warning"
                    onClose={() => setShowBalanceAlert(false)}
                >
                    You have an outstanding balance. Please complete the payment.
                </NativeAlert>

                <NativeAlert
                    open={alertMsg.open}
                    severity={alertMsg.severity}
                    topClass="top-20"
                    onClose={() => setAlertMsg({ ...alertMsg, open: false })}
                >
                    {alertMsg.message}
                </NativeAlert>

                <NativeModal
                    open={showNavigationDialog}
                    onClose={() => setShowNavigationDialog(false)}
                    title="Payment Required"
                    footer={(
                        <button
                            type="button"
                            onClick={() => setShowNavigationDialog(false)}
                            className="rounded-lg bg-blue-600 px-6 py-2 font-semibold text-white"
                        >
                            Stay on Page
                        </button>
                    )}
                >
                    <div className="flex items-center space-x-3">
                        <Lock className="text-yellow-500" size={24} />
                        <span className="text-gray-700">You must complete the payment before leaving this page.</span>
                    </div>
                </NativeModal>
                <CashPaymentDialog
                    open={showCashConfirmDialog}
                    isSubmitting={isSubmittingOfflinePayment}
                    onClose={() => setShowCashConfirmDialog(false)}
                    onConfirm={() => handleOfflinePaymentRequest(availablePaymentOptions.find((option) => isCashPaymentOption(option)))}
                />
                <ZellePaymentDialog
                    open={showZelleModal}
                    onClose={() => setShowZelleModal(false)}
                    isSubmitting={isSubmittingOfflinePayment}
                    manualPaymentDetails={manualPaymentDetails}
                    currencySymbol={currencySymbol()}
                    totalAmt={totalAmt}
                    orderNumber={orderDetails?.orderDetails?.[0]?.number}
                    referenceId={zelleReferenceId}
                    onReferenceIdChange={setZelleReferenceId}
                    normalizedReferenceId={normalizedZelleReferenceId}
                    onConfirm={() => handleOfflinePaymentRequest(manualPaymentOption)}
                />

                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center text-gray-700 space-x-2 mb-8">
                        <button
                            className={`text-[#3B4B5E] hover:text-[#2C3E50] flex items-center transition ${blockNavigation ? 'cursor-not-allowed opacity-50' : ''}`}
                            onClick={handleBackNavigation}
                            disabled={blockNavigation && !isProcessingPayment}
                        >
                            <ArrowLeft className="mr-1" size={20} />
                            <span className="text-sm font-medium">Back</span>
                        </button>
                        <span className="text-gray-400">/</span>
                        <span className="font-semibold text-sm uppercase tracking-wider">{currentPageName}</span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl shadow-lg p-6 transition duration-300 hover:shadow-xl">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-2xl sm:text-3xl font-bold text-[#3B4B5E]">
                                        Order Summary
                                    </h2>
                                    <span className="rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-700">
                                        Order #{orderDetails.orderDetails[0]?.number || 'N/A'}
                                    </span>
                                </div>

                                {(paymentUser?.firstName || paymentUser?.email) && (
                                    <div className="bg-[#F0F4F8] p-6 rounded-lg mb-6 border border-gray-200">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-[#2C3E50]">Student Details</h3>
                                            <span title="Verified Student">
                                                <CheckCircle className="text-green-500" />
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                            <div>
                                                <p className="font-medium">Name</p>
                                                <p>{paymentUser.firstName} {paymentUser.lastName}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Application No</p>
                                                <p>{paymentUser.primaryId || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Email</p>
                                                <p>{paymentUser.email || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Phone</p>
                                                <p>{paymentUser.phoneNumber || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Payment Method</h3>
                                    <div className="bg-[#F0F4F8] p-6 rounded-lg border border-gray-200">
                                        <PaymentMethodSelector
                                            availablePaymentOptions={availablePaymentOptions}
                                            paymentMethod={paymentMethod}
                                            onPaymentMethodChange={setPaymentMethod}
                                            selectedPaymentOption={selectedPaymentOption}
                                            manualPaymentDetails={manualPaymentDetails}
                                            onOpenManualPaymentModal={() => setShowZelleModal(true)}
                                        />
                                    </div>
                                </div>

                                <div className="border-t border-gray-200 pt-6">
                                    <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Payment Breakdown</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Subtotal:</span>
                                            <span className="font-medium">{currencySymbol()}{subtotal.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Tax:</span>
                                            <span className="font-medium">{currencySymbol()}{tax.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Processing Fee:</span>
                                            <span className="font-medium">{currencySymbol()}0.00</span>
                                        </div>
                                        <InlineDivider />
                                        <div className="flex justify-between text-lg font-bold text-[#2C3E50]">
                                            <span>Total Amount:</span>
                                            <span>{currencySymbol()}{totalAmt.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8 transition duration-300 hover:shadow-xl">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-[#3B4B5E]">Payment</h3>
                                    <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                                        Secure
                                    </span>
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                        <div className="flex items-center space-x-3">
                                            <Lock className="text-blue-500 text-xl" />
                                            <p className="text-sm text-blue-700">
                                                Your payment information is processed securely. We do not store your payment details.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-200 pt-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-gray-600">Amount Due:</span>
                                            <span className="text-xl font-bold text-[#3B4B5E]">
                                                {currencySymbol()}{totalAmt.toFixed(2)}
                                            </span>
                                        </div>

                                        <button
                                            onClick={handleCheckout}
                                            disabled={isPaymentActionDisabled}
                                            className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg ${isPaymentActionDisabled ? 'cursor-not-allowed' : ''}`}
                                            style={{ backgroundColor: isPaymentActionDisabled ? '#60a5fa' : 'var(--education-secondary, #3B9394)' }}
                                        >
                                            {isProcessingPayment ? (
                                                <>
                                                    <Loader2 size={20} className="mr-2 animate-spin" />
                                                    {getCheckoutButtonLabel()}
                                                </>
                                            ) : (
                                                <>
                                                    {requiresOfflineReference(selectedPaymentOption) ? <QrCode className="mr-2" /> : <CreditCard className="mr-2" />}
                                                    {getCheckoutButtonLabel()}
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div className="text-xs text-gray-500 text-center pt-2">
                                        By completing this payment, you agree to our Terms of Service and Privacy Policy.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Default UI for other domains
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 font-sans">
            <div className="max-w-7xl mx-auto mt-12">
                <div className="flex items-center mb-8">
                    <button
                        className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg cursor-pointer hover:bg-blue-700 transition duration-200 ease-in-out mr-4 text-lg flex items-center shadow-md"
                        onClick={handleBackNavigation}
                        disabled={loading || isProcessingPayment}
                    >
                        <ArrowLeft className="mr-2" />
                        Back
                    </button>
                </div>

                {loading && <Loading message="Processing payment, please wait..." />}

                <NativeAlert
                    open={alertMsg.open}
                    severity={alertMsg.severity}
                    onClose={() => setAlertMsg({ ...alertMsg, open: false })}
                >
                    {alertMsg.message}
                </NativeAlert>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-md overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                    <ReceiptText className="mr-2 text-blue-500" />
                                    {businessdomain === "RETAIL" ? "Order Summary" : "Payment Summary"}
                                </h2>
                            </div>

                            <div className="p-6">
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Order Details</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-sm text-gray-500">Order Number</p>
                                                <p className="font-medium">{orderDetails.orderDetails[0]?.number || `ORD-${orderDetails.orderDetails[0]?.primaryId}`}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500">Date</p>
                                                <p className="font-medium">
                                                    {new Date().toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Payment Method</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <PaymentMethodSelector
                                            availablePaymentOptions={availablePaymentOptions}
                                            paymentMethod={paymentMethod}
                                            onPaymentMethodChange={setPaymentMethod}
                                            selectedPaymentOption={selectedPaymentOption}
                                            manualPaymentDetails={manualPaymentDetails}
                                            onOpenManualPaymentModal={() => setShowZelleModal(true)}
                                        />
                                    </div>
                                </div>

                                {businessdomain === "RETAIL" && (
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-3">Shipping Address</h3>
                                        <div className="bg-gray-50 p-4 rounded-lg">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <p className="font-medium">{shippingAddress.firstName} {shippingAddress.lastName}</p>
                                                    <p className="text-gray-600">{shippingAddress.flatApartment}</p>
                                                    <p className="text-gray-600">{shippingAddress.street1}</p>
                                                    <p className="text-gray-600">{shippingAddress.city}, {shippingAddress.state} {shippingAddress.pinCode}</p>
                                                    <p className="text-gray-600">{shippingAddress.country}</p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-600">
                                                        <span className="font-medium">Phone:</span> {shippingAddress.phoneNumber}
                                                    </p>
                                                    <p className="text-gray-600">
                                                        <span className="font-medium">Email:</span> {shippingAddress.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-8">
                            <div className="p-6 bg-gray-800 text-white">
                                <h3 className="text-xl font-bold flex items-center">
                                    <CreditCard className="mr-2" />
                                    Payment Summary
                                </h3>
                            </div>

                            <div className="p-6">
                                <div className="space-y-3 mb-6">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="font-medium">{currencySymbol()}{subtotal.toFixed(2)}</span>
                                    </div>
                                    {businessdomain === "RETAIL" && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Shipping</span>
                                            <span className="font-medium">{currencySymbol()}{shippingCharge.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Tax</span>
                                        <span className="font-medium">{currencySymbol()}{tax.toFixed(2)}</span>
                                    </div>
                                    <InlineDivider />
                                    <div className="flex justify-between text-lg font-bold text-gray-800">
                                        <span>Total</span>
                                        <span>{currencySymbol()}{totalAmt.toFixed(2)}</span>
                                    </div>
                                </div>

                                <button
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg text-lg font-semibold flex items-center justify-center transition duration-300 shadow-md hover:shadow-lg disabled:opacity-70"
                                    onClick={handleCheckout}
                                    disabled={isPaymentActionDisabled}
                                >
                                    {isProcessingPayment ? (
                                        <>
                                            <Loader2 size={20} className="mr-2 animate-spin" />
                                            {getCheckoutButtonLabel()}
                                        </>
                                    ) : (
                                        <>
                                            {requiresOfflineReference(selectedPaymentOption) ? <QrCode className="mr-2" /> : <CreditCard className="mr-2" />}
                                            {getCheckoutButtonLabel()}
                                        </>
                                    )}
                                </button>

                                <div className="mt-4 text-center text-sm text-gray-500">
                                    <p>By completing your purchase, you agree to our Terms of Service</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <CashPaymentDialog
                    open={showCashConfirmDialog}
                    isSubmitting={isSubmittingOfflinePayment}
                    onClose={() => setShowCashConfirmDialog(false)}
                    onConfirm={() => handleOfflinePaymentRequest(availablePaymentOptions.find((option) => isCashPaymentOption(option)))}
                />
                <ZellePaymentDialog
                    open={showZelleModal}
                    onClose={() => setShowZelleModal(false)}
                    isSubmitting={isSubmittingOfflinePayment}
                    manualPaymentDetails={manualPaymentDetails}
                    currencySymbol={currencySymbol()}
                    totalAmt={totalAmt}
                    orderNumber={orderDetails?.orderDetails?.[0]?.number}
                    referenceId={zelleReferenceId}
                    onReferenceIdChange={setZelleReferenceId}
                    normalizedReferenceId={normalizedZelleReferenceId}
                    onConfirm={() => handleOfflinePaymentRequest(manualPaymentOption)}
                />
            </div>
        </div>
    );
};

export default OtpPayment;
